import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { publicIdSchema } from "./public-id";

const id = publicIdSchema;
export const templateProposalInputSchema = z.strictObject({
  instruction: z.string().trim().min(1).max(4000),
  templatePublicId: id.optional(),
  current: z
    .strictObject({
      name: z.string().max(200),
      headers: z.array(z.string()).max(40),
      rules: z.string().max(10000),
    })
    .optional(),
});
export const templateProposalDraftSchema = z.object({
  name: z.string().max(100),
  headers: z.array(z.string().max(100)).max(20),
  rules: z.string(),
});
export const templateProposalResultSchema = z.object({
  proposal: templateProposalDraftSchema,
  summaryOfChanges: z.string(),
});
export const templateProposalSchema = templateProposalResultSchema
  .extend({
    templatePublicId: id.nullable(),
    baseVersion: z
      .string()
      .regex(/^[0-9a-f]{64}$/)
      .nullable(),
    diff: z.object({
      name: z.object({
        before: z.string().nullable(),
        after: z.string(),
        changed: z.boolean(),
      }),
      headers: z.object({
        before: z.array(z.string()),
        after: z.array(z.string()),
        added: z.array(z.string()),
        removed: z.array(z.string()),
        reordered: z.boolean(),
      }),
      rules: z.object({
        before: z.string().nullable(),
        after: z.string(),
        changed: z.boolean(),
      }),
    }),
  })
  .refine(
    (value) =>
      (value.templatePublicId === null) === (value.baseVersion === null),
    "Stored template version is required",
  );
export type TemplateProposalBody = z.output<typeof templateProposalInputSchema>;
export type TemplateProposalDraft = z.output<
  typeof templateProposalDraftSchema
>;
export type TemplateProposalResult = z.output<
  typeof templateProposalResultSchema
>;
export const templateProposalContract = {
  createTemplateProposal: oc
    .errors({
      BAD_REQUEST: { message: "Invalid request" },
      UNAUTHORIZED: { message: "Authentication required" },
      FORBIDDEN: { message: "Account required" },
      NOT_FOUND: { message: "Template not found" },
      METHOD_NOT_ALLOWED: { message: "Method Not Allowed" },
      INTERNAL_SERVER_ERROR: { message: "Internal Server Error" },
      SERVICE_UNAVAILABLE: { message: "Template proposal unavailable" },
    })
    .input(
      z.object({
        query: z.strictObject({}).default({}),
        body: templateProposalInputSchema,
      }),
    )
    .meta(
      openapi({
        method: "POST",
        path: "/api/v2/template-proposals",
        inputStructure: "detailed",
        successStatus: 200,
        spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
      }),
    )
    .output(templateProposalSchema),
};
